import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components6';
import { When } from 'react-if';
import type { PluginMedia } from 'rwl-shared/dist/models/plugin/Plugin';
import { ButtonContainer, NextButton, PrevButton } from './CarouselButtons';
import { mqDown, mqUp } from '../../../../theme';
import { CarouselDots } from './CarouselDots';
import { CarouselCounts } from './CarouselCounts';

export type MediaVariants = 'light' | 'dark';

const CarouselContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
    //if browser has the ability to hover (has a mouse), then show the buttons on hover
    @media (hover: hover) {
        &:hover {
            ${ButtonContainer} {
                visibility: visible;
                opacity: 1;
            }
        }
    }
`;

const CarouselImage = styled.img`
    border-radius: var(--rwl-spacing-sm) 0 0 var(--rwl-spacing-sm);
    height: 100%;
    width: var(--rwl-jobCardMediaWidth-lg);
    object-fit: cover;
    ${mqUp('xl')} {
        aspect-ratio: 50/43;
    }
    ${mqDown('xl')} {
        aspect-ratio: 42/50;
        width: var(--rwl-jobCardMediaWidth-md);
    }
    ${mqDown('sm')} {
        aspect-ratio: 1/1;
        width: var(--rwl-jobCardMediaWidth-sm);
    }
    ${mqDown('xs')} {
        aspect-ratio: 35/50;
        width: var(--rwl-jobCardMediaWidth-xs);
    }
`;

type CarouselProps = {
    disabledCarousel: boolean;
    images: PluginMedia[];
    variant?: MediaVariants;
    onChange?: (media: PluginMedia, index: number) => void;
};

const generateImageListHash = (media: PluginMedia[]): string => {
    // This sort just needs to be stable, not necessarily the same as the order in the UI
    // Going to lexical sort the ids
    // Then join them to form a unique string that will tell us if the list has changed
    return [...media]
        .map((m) => m.id)
        .sort()
        .join(',');
};

export const MediaCarousel = ({
    disabledCarousel,
    images,
    variant = 'light',
    onChange,
}: CarouselProps): JSX.Element => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [touchStartX, setTouchStartX] = useState<number | null>(null);
    const imageListHash = useMemo(
        () => generateImageListHash(images),
        [images]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: we're triggering a side effect when the image list changes
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [imageListHash]);

    const nextImage = (e: any) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
        onChange?.(images[currentImageIndex], currentImageIndex);
    };

    const prevImage = (e: any) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        setCurrentImageIndex(
            (prevIndex) => (prevIndex - 1 + images.length) % images.length
        );
        onChange?.(images[currentImageIndex], currentImageIndex);
    };

    const handleTouchStart = (e: any) => {
        setTouchStartX(e.touches[0].clientX);
    };

    const handleTouchMove = (e: any) => {
        if (touchStartX !== null) {
            const currentX = e.touches[0].clientX;
            const diff = touchStartX - currentX;

            if (diff > 5) {
                nextImage(e);
            } else if (diff < -5) {
                prevImage(e);
            }
            setTouchStartX(null);
        }
    };

    return (
        <CarouselContainer
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
        >
            <CarouselImage
                loading="lazy"
                className="carousel-image"
                src={images[currentImageIndex].thumbnailUrl}
                alt="carousel"
            />
            <When condition={images.length > 1 && !disabledCarousel}>
                <ButtonContainer variant={variant}>
                    <PrevButton onButtonClick={prevImage} variant={variant} />
                    <NextButton onButtonClick={nextImage} variant={variant} />
                </ButtonContainer>
                <When condition={variant === 'light'}>
                    <CarouselDots
                        totalImages={images.length}
                        currentImageIndex={currentImageIndex}
                        setCurrentImageIndex={setCurrentImageIndex}
                    />
                </When>
                <When condition={variant === 'dark'}>
                    <CarouselCounts
                        totalImages={images.length}
                        currentImageIndex={currentImageIndex}
                    />
                </When>
            </When>
        </CarouselContainer>
    );
};
